/**
 * Scene Details Modal Component
 * Refined right sidebar with clean section headers and essential information
 */

import React from 'react';
import { Modal, Space, Divider, Typography, Button, Badge } from 'antd';
import { 
  FileText, 
  Layout, 
  Palette, 
  Film, 
  Clock,
  Tag,
  FileEdit,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Scene } from '../../../../types/scene';
import { SceneTagSelector } from './SceneCard/SceneTagSelector';
import { SceneNotesField } from './SceneCard/SceneNotesField';
import './SceneDetailsModal.scss';

const { Title, Text } = Typography;

interface SceneDetailsModalProps {
  scene: Scene | null;
  visible: boolean;
  onClose: () => void;
  onTagsChange?: (tags: any[]) => void;
  onNotesChange?: (notes: string) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  readOnly?: boolean;
  canvasRatio?: string;
  selectedBackground?: any;
}

export const SceneDetailsModal: React.FC<SceneDetailsModalProps> = ({
  scene,
  visible,
  onClose,
  onTagsChange,
  onNotesChange,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  readOnly = false,
  canvasRatio = '16:9',
  selectedBackground
}) => {
  if (!scene) return null;

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getScriptPreview = () => {
    if (!scene.script) return 'No script configured';
    
    const lines = scene.script.split('\n').slice(0, 4);
    const preview = lines.join('\n');
    
    if (preview.length > 200) {
      return preview.substring(0, 200) + '...';
    }
    
    return preview;
  };

  const getBackgroundInfo = () => {
    const background = selectedBackground || scene.background;
    
    if (!background) {
      return { text: 'No background', visual: null };
    }

    switch (background.type) {
      case 'solid':
        return {
          text: `Solid Color: ${background.colors?.[0] || 'N/A'}`,
          visual: (
            <div 
              className="background-preview solid"
              style={{ backgroundColor: background.colors?.[0] }}
            />
          )
        };
      case 'color': {
        const colorVal = background.value || '#000000';
        return {
          text: `Solid Color: ${colorVal}`,
          visual: (
            <div 
              className="background-preview solid"
              style={{ backgroundColor: colorVal }}
            />
          )
        };
      }
      case 'gradient':
        return {
          text: `Gradient (${background.colors?.length || 0} colors)`,
          visual: (
            <div 
              className="background-preview gradient"
              style={{ 
                background: `linear-gradient(135deg, ${background.colors?.join(', ')})` 
              }}
            />
          )
        };
      case 'image':
        return {
          text: 'Background Image',
          visual: (
            <div 
              className="background-preview image"
              style={{ 
                backgroundImage: `url(${background.imageUrl})` 
              }}
            />
          )
        };
      default:
        return { text: 'Unknown', visual: null };
    }
  };

  const getStatusBadge = () => {
    switch (scene.status) {
      case 'recorded':
        return <Badge status="success" text="Recorded" />;
      case 'recording':
        return <Badge status="processing" text="Recording" />;
      case 'failed':
        return <Badge status="error" text="Failed" />;
      case 'pending':
      default:
        return <Badge status="default" text="Pending" />;
    }
  };

  const backgroundInfo = getBackgroundInfo();
  const hasRecordings = scene.recordingHistory && scene.recordingHistory.length > 0;
  const takesCount = scene.recordingHistory?.length || 0;
  const keptTakes = scene.recordingHistory?.filter(t => t.isKept).length || 0;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="scene-details-modal"
      closeIcon={<X size={20} />}
      title={
        <div className="modal-header">
          <div className="modal-title-section">
            <Text strong className="scene-number">Scene #{scene.order + 1}</Text>
            <Title level={4} style={{ margin: 0 }}>{scene.title || 'Untitled Scene'}</Title>
          </div>
          <div className="modal-navigation">
            {hasPrevious && (
              <Button 
                type="text" 
                icon={<ChevronLeft size={18} />} 
                onClick={onPrevious}
                disabled={!hasPrevious}
              >
                Previous
              </Button>
            )}
            {hasNext && (
              <Button 
                type="text" 
                icon={<ChevronRight size={18} />} 
                onClick={onNext}
                disabled={!hasNext}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="scene-details-content">
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          
          {/* Status & Duration */}
          <div className="detail-section">
            <div className="section-header">
              <div className="icon-wrapper">
                <Clock size={16} />
              </div>
              <span className="section-title">Status & Duration</span>
            </div>
            <div className="section-content">
              <div className="status-duration-grid">
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className="info-value">{getStatusBadge()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Duration:</span>
                  <span className="info-value">
                    {scene.duration > 0 ? formatDuration(scene.duration) : 'Not recorded'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Layout Section */}
          <div className="detail-section">
            <div className="section-header">
              <div className="icon-wrapper">
                <Layout size={16} />
              </div>
              <span className="section-title">Layout</span>
            </div>
            <div className="section-content">
              <div className="layout-grid">
                <div className="info-row">
                  <span className="info-label">Preset:</span>
                  <span className="info-value">{scene.layoutPreset || 'Not configured'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Aspect Ratio:</span>
                  <span className="info-value">{canvasRatio}</span>
                </div>
              </div>
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Background Section */}
          <div className="detail-section">
            <div className="section-header">
              <div className="icon-wrapper">
                <Palette size={16} />
              </div>
              <span className="section-title">Background</span>
            </div>
            <div className="section-content">
              <div className="background-info">
                {backgroundInfo.visual}
                <span className="info-text">{backgroundInfo.text}</span>
              </div>
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Script Section (Synced with Teleprompter) */}
          <div className="detail-section">
            <div className="section-header">
              <div className="icon-wrapper">
                <FileText size={16} />
              </div>
              <span className="section-title">Script</span>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                Synced with Teleprompter
              </Text>
            </div>
            <div className="section-content script-preview">
              {getScriptPreview()}
            </div>
            {scene.script && scene.script.length > 200 && (
              <div className="script-meta">
                {scene.script.split('\n').length} lines • {scene.script.length} characters
              </div>
            )}
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Tags Section */}
          {((scene.tags && scene.tags.length > 0) || !readOnly) && (
            <>
              <div className="detail-section">
                <div className="section-header">
                  <div className="icon-wrapper">
                    <Tag size={16} />
                  </div>
                  <span className="section-title">Tags</span>
                </div>
                <div className="section-content">
                  <SceneTagSelector
                    selectedTags={scene.tags || []}
                    onChange={(tags) => onTagsChange?.(tags)}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <Divider style={{ margin: 0 }} />
            </>
          )}

          {/* Production Notes Section */}
          {(scene.notes || !readOnly) && (
            <>
              <div className="detail-section">
                <div className="section-header">
                  <div className="icon-wrapper">
                    <FileEdit size={16} />
                  </div>
                  <span className="section-title">Production Notes</span>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                    Internal notes for this scene
                  </Text>
                </div>
                <div className="section-content">
                  <SceneNotesField
                    value={scene.notes}
                    onChange={(notes) => onNotesChange?.(notes)}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <Divider style={{ margin: 0 }} />
            </>
          )}

          {/* Recording History (if any) */}
          {hasRecordings && (
            <div className="detail-section">
              <div className="section-header">
                <div className="icon-wrapper">
                  <Film size={16} />
                </div>
                <span className="section-title">Recording History</span>
              </div>
              <div className="section-content">
                <div className="recording-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Takes</span>
                    <span className="stat-value">{takesCount}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Kept</span>
                    <span className="stat-value success">{keptTakes}</span>
                  </div>
                  {scene.duration > 0 && (
                    <div className="stat-item">
                      <span className="stat-label">Final Duration</span>
                      <span className="stat-value">{formatDuration(scene.duration)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Space>
      </div>
    </Modal>
  );
};
